import { type QueryClient, useQuery } from "@tanstack/react-query";
import { api, ApiError, qs, request } from "../client";
import type { Segment, SegmentKind, SegmentPage, SegmentState, TrackDetection } from "../types";

export interface SegmentFilters {
  q?: string;
  kind?: SegmentKind | null;
  state?: SegmentState | null;
  starred?: boolean | null;
  unlinked?: boolean | null;
  autoApproved?: boolean | null;
  /** True for audio that has aged out of the archive, false for what is still playable. */
  expired?: boolean | null;
  /** What the calling account has heard. Inert when signed out; there is no tracker then. */
  listened?: "yes" | "no" | null;
  /** With `listened: "no"`, the highest percentage still counted as unlistened. */
  listenedMax?: number | null;
  /** False excludes whatever is still running. */
  onAir?: boolean | null;
  /** ISO instant; rows whose span reaches past it. Paired with `end` it is an overlap. */
  start?: string | null;
  /** ISO instant, exclusive; rows whose span begins before it. */
  end?: string | null;
  limit?: number;
  offset?: number;
  order?: "start_asc" | "start_desc";
  /**
   * False collapses a joined show into its first section, which is what a listener browsing
   * the archive wants. Left alone every section is listed, which is what editing one needs.
   */
  joined?: boolean;
}

/**
 * The filter under the names the API reads it by, off the query string or out of a body.
 *
 * One mapping with two callers -- the listing below and the listening session further down --
 * because the queue the player walks has to be the listing the listener is looking at, and a
 * second translation of the same panel is exactly how those two drift apart.
 */
function segmentParams(filters: SegmentFilters) {
  return {
    q: filters.q,
    kind: filters.kind,
    state: filters.state,
    starred: filters.starred,
    unlinked: filters.unlinked,
    auto_approved: filters.autoApproved,
    expired: filters.expired,
    listened: filters.listened,
    listened_max: filters.listenedMax,
    on_air: filters.onAir,
    joined: filters.joined,
    start: filters.start,
    end: filters.end,
  };
}

/**
 * The tracklist filter as composed playback takes it: the same parameters the listing reads
 * off the query string, as JSON, with anything unset dropped rather than sent as null.
 *
 * The server resolves the queue from this and advances it itself, so a row that appears after
 * the session opens is still picked up -- which a frozen list of ids could not manage, and
 * which "the player plays what is currently filtered in the tracklist" requires.
 */
export function listenFilter(filters: SegmentFilters): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(segmentParams(filters)).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  ) as Record<string, string | number | boolean>;
}

export interface MediaPiece {
  media_start_us: number;
  media_end_us: number;
  asset_id: string;
  byte_start: number;
  byte_end: number;
  samples: number;
  sample_rate: number;
  source_start: string | null;
  source_end: string | null;
  segment_id: number | null;
  discontinuity: boolean;
}

export interface MediaItem {
  segment_id: number | null;
  media_start_us: number;
  media_end_us: number | null;
  item_offset_us?: number;
}

export interface MediaMap {
  pieces: MediaPiece[];
  items: MediaItem[];
}

export interface PlaybackQueueItem {
  segment_id: number | null;
  source_id: number;
  kind: SegmentKind | null;
  span_start: string;
  span_end: string | null;
  content_start: string;
  content_end: string | null;
  item_offset_us?: number;
  title: string | null;
  djs: string | null;
  artist: string | null;
  member_ids: number[];
  excisions: Array<{ start: string; end: string }>;
}

export type PlaybackRequestSource =
  | { kind: "segment"; segment_id: number; at?: string; item_position_us?: number }
  | { kind: "window"; at: string }
  | { kind: "live"; at?: string };

export interface PlaybackPresentation {
  id: string;
  state: string;
  mode: "archive" | "live";
  playlist_url: string;
  epoch: string;
  start_media_us: number;
  duration_us: number;
  ended: boolean;
  items: PlaybackQueueItem[];
  map: MediaMap;
  omitted: Array<{ segment_id: number | null; reason: string }>;
  error_code: string | null;
  expires_at: string;
}

export function startPlayback(
  filter: Record<string, string | number | boolean>,
  source: PlaybackRequestSource,
  signal?: AbortSignal,
): Promise<PlaybackPresentation> {
  return request<PlaybackPresentation>("/api/playback", {
    method: "POST",
    body: JSON.stringify({ filter, source }),
    signal,
  });
}

export function getPlayback(
  id: string,
  signal?: AbortSignal,
  atMediaUs?: number,
): Promise<PlaybackPresentation> {
  const demand =
    atMediaUs === undefined ? "" : `?at_media_us=${Math.max(0, Math.round(atMediaUs))}`;
  return request<PlaybackPresentation>(`/api/playback/${encodeURIComponent(id)}${demand}`, {
    signal,
  });
}

export function useSegments(filters: SegmentFilters) {
  const query = qs({
    ...segmentParams(filters),
    limit: filters.limit ?? 50,
    offset: filters.offset ?? 0,
    order: filters.order ?? "start_desc",
  });
  return useQuery({
    queryKey: ["segments", query],
    queryFn: () => api.get<SegmentPage>(`/api/segments${query}`),
    placeholderData: (previous) => previous,
  });
}

/**
 * The segments overlapping a single instant.
 *
 * A window playlist is an arbitrary stretch of archive, so its source cannot say what is
 * playing the way a segment source can; this is how that gets a name. Asked for a handful
 * rather than one, because a show and the tracks inside it overlap and the caller decides
 * which of them describes the instant best.
 */
function segmentsAtOptions(ms: number) {
  const query = qs({
    start: new Date(ms).toISOString(),
    // An instant, expressed as the shortest range the overlap filter accepts.
    end: new Date(ms + 1).toISOString(),
    limit: 8,
    order: "start_desc",
  });
  return {
    queryKey: ["segments-at", query],
    queryFn: () => api.get<SegmentPage>(`/api/segments${query}`),
    // Boundaries move while a show is provisional, but not on the timescale of a listen,
    // and this only labels what is already playing.
    staleTime: 60_000,
  };
}

export function useSegmentsAt(ms: number | null) {
  // The disabled key is never fetched, so the instant it names does not matter; building it
  // unconditionally is what keeps one description of this query in one place.
  return useQuery({ ...segmentsAtOptions(ms ?? 0), enabled: ms !== null });
}

/**
 * Put the answer for an instant in the cache before handing the player a window source.
 *
 * Window playback has no name of its own: the player looks one up through the hook above, and
 * on a cold cache that lookup is a round trip during which it has nothing to show but a
 * placeholder. So whoever starts the playback resolves the instant first -- the same bargain
 * the timeline already makes for a block it can name by id.
 */
export function fetchSegmentsAt(client: QueryClient, ms: number): Promise<SegmentPage> {
  return client.fetchQuery(segmentsAtOptions(ms));
}

export function useSegment(id: number | null) {
  return useQuery({
    queryKey: ["segment", id],
    queryFn: async () => {
      try {
        return await api.get<Segment>(`/api/segments/${id}`);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: id !== null,
  });
}

/**
 * What the fingerprinter heard over one matched track's stretch, best score first.
 *
 * Asked only for the block under the pointer, and never for an unmatched one -- the server
 * answers those with an empty list, but a request per hover across a lane of jukebox tracks
 * is a request per hover for nothing.
 *
 * Cached indefinitely: a detection row is written once by the scan that found it and does
 * not move afterwards.
 */
export function useMatches(id: number | null, matched: boolean) {
  return useQuery({
    queryKey: ["matches", id],
    queryFn: () => api.get<TrackDetection[]>(`/api/segments/${id}/matches`),
    enabled: matched && id !== null,
    staleTime: Infinity,
  });
}
